/**
 * DescartarModal.js — Modal de descarte con feedback estructurado
 *
 * Cuando el analista descarta una oportunidad, captura:
 * 1. Motivo principal (select obligatorio)
 * 2. Detalle libre (textarea opcional)
 * 3. Feedback sobre el score (radio: correcto / demasiado alto / demasiado bajo)
 *
 * Estos datos se envían al backend junto con un snapshot del scoring actual,
 * permitiendo aprendizaje futuro del algoritmo.
 *
 * Fase 1 del sistema de feedback — staging only.
 */
import { useState } from "react";
import api from "@/services/api";
import {
  XCircle,
  AlertTriangle,
  MessageSquare,
  TrendingUp,
  ChevronDown,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

// Motivos de descarte — códigos alineados con el backend
const MOTIVOS_DESCARTE = [
  { value: "sector_no_aplica", label: "No es nuestro sector" },
  { value: "importe_bajo", label: "Importe demasiado bajo" },
  { value: "importe_alto", label: "Importe fuera de rango (alto)" },
  { value: "competencia_fuerte", label: "Competencia demasiado fuerte" },
  { value: "adjudicatario_conocido", label: "Ya adjudicado a empresa conocida" },
  { value: "requisitos_incompatibles", label: "Requisitos técnicos incompatibles" },
  { value: "zona_no_viable", label: "Zona geográfica no viable" },
  { value: "plazo_ajustado", label: "Plazo demasiado ajustado" },
  { value: "baja_subcontratacion", label: "Baja probabilidad de subcontratación" },
  { value: "duplicado", label: "Duplicado / ya gestionado" },
  { value: "otro", label: "Otro motivo" },
];

// Opciones de feedback sobre el score
const SCORE_FEEDBACK_OPTIONS = [
  { value: "correcto", label: "Sí, score adecuado", color: "text-green-400" },
  { value: "demasiado_alto", label: "Score demasiado alto (no debería haber llegado aquí)", color: "text-orange-400" },
  { value: "demasiado_bajo", label: "Score demasiado bajo (casi la paso por alto)", color: "text-cyan-400" },
];

export default function DescartarModal({ open, onClose, oportunidad, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [motivoDescarte, setMotivoDescarte] = useState("");
  const [detalle, setDetalle] = useState("");
  const [scoreFeedback, setScoreFeedback] = useState("");

  const handleSubmit = async () => {
    if (!motivoDescarte) {
      toast.error("Selecciona un motivo de descarte");
      return;
    }

    setLoading(true);

    try {
      // Construir el objeto de feedback con snapshot del scoring
      const feedback = {
        motivo_descarte: motivoDescarte,
        detalle: detalle.trim() || null,
        score_feedback: scoreFeedback || null,
        // Snapshot del estado actual para análisis futuro
        score_at_decision: oportunidad.score || 0,
        pain_score_at_decision: oportunidad.pain_analysis?.pain_score || null,
        keywords_at_decision: oportunidad.keywords_detectados || [],
        cpv_at_decision: oportunidad.cpv_principal || null,
        tipo_srs_at_decision: oportunidad.tipo_srs || null,
        importe_at_decision: oportunidad.importe || null,
      };

      await api.put(
        `/oportunidades/${oportunidad.oportunidad_id}`,
        {
          estado: "descartada",
          feedback,
        }
      );

      toast.success("Oportunidad descartada con feedback registrado");

      // Reset form
      setMotivoDescarte("");
      setDetalle("");
      setScoreFeedback("");

      onSuccess?.();
    } catch (error) {
      const message = error.response?.data?.detail || "Error al descartar";
      toast.error(message);
      console.error("Error descartando:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!oportunidad) return null;

  const motivoLabel = MOTIVOS_DESCARTE.find(m => m.value === motivoDescarte)?.label;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-white/10 !max-w-[540px] w-[95vw] max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b border-white/10 shrink-0">
          <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-400" />
            Descartar Oportunidad
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 space-y-5">
          {/* Resumen de la oportunidad */}
          <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
            <p className="font-medium text-white text-sm truncate">{oportunidad.titulo}</p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <Badge className="bg-slate-600 text-white text-xs">
                Score: {oportunidad.score}
              </Badge>
              {oportunidad.tipo_srs && (
                <Badge variant="outline" className="border-cyan-500/30 text-cyan-400 text-xs">
                  {oportunidad.tipo_srs}
                </Badge>
              )}
              {oportunidad.importe && (
                <Badge variant="outline" className="border-green-500/30 text-green-400 text-xs">
                  {new Intl.NumberFormat("es-ES", {
                    style: "currency",
                    currency: "EUR",
                    minimumFractionDigits: 0,
                  }).format(oportunidad.importe)}
                </Badge>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="flex items-start gap-2 p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
            <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
            <p className="text-xs text-orange-300">
              Tu feedback ayuda a mejorar el algoritmo de scoring.
              Cuanta más información, mejor serán las futuras recomendaciones.
            </p>
          </div>

          {/* 1. Motivo de descarte (obligatorio) */}
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm font-semibold flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
              Motivo principal *
            </Label>
            <div className="relative">
              <select
                value={motivoDescarte}
                onChange={(e) => setMotivoDescarte(e.target.value)}
                className="w-full h-10 px-3 pr-8 rounded-md bg-slate-950 border border-slate-700 text-white text-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500/50"
              >
                <option value="" disabled>
                  Selecciona un motivo...
                </option>
                {MOTIVOS_DESCARTE.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* 2. Detalle (opcional) */}
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
              Detalle (opcional)
            </Label>
            <Textarea
              value={detalle}
              onChange={(e) => setDetalle(e.target.value)}
              placeholder="Ej: Indra siempre gana estos contratos del SEPE..."
              className="bg-slate-950 border-slate-700 min-h-[70px] text-sm"
            />
          </div>

          {/* 3. Feedback del score */}
          <div className="space-y-3">
            <Label className="text-slate-300 text-sm font-semibold flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-cyan-400" />
              ¿El score era correcto?
            </Label>
            <div className="space-y-2">
              {SCORE_FEEDBACK_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors border ${
                    scoreFeedback === opt.value
                      ? "bg-slate-800 border-cyan-500/50"
                      : "bg-slate-800/30 border-transparent hover:bg-slate-800/60 hover:border-slate-700"
                  }`}
                >
                  <input
                    type="radio"
                    name="score_feedback"
                    value={opt.value}
                    checked={scoreFeedback === opt.value}
                    onChange={(e) => setScoreFeedback(e.target.value)}
                    className="w-4 h-4 accent-cyan-500"
                  />
                  <span className={`text-sm ${opt.color}`}>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="p-6 pt-4 border-t border-white/10 bg-slate-900/50 shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="border-slate-700 text-slate-300 hover:bg-white/5"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !motivoDescarte}
            className="bg-gradient-to-r from-red-600 to-orange-600 text-white hover:from-red-700 hover:to-orange-700 transition"
          >
            {loading ? (
              "Descartando..."
            ) : (
              <>
                <XCircle className="w-4 h-4 mr-2" />
                Descartar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
